import { useMemo, useState } from 'react';
import {
  HOME3_STATE_LABEL_KEY,
  agentCount,
  home3StateBadge,
  emptyFeedMessage,
  feedRowCap,
  filterFeed,
  formatFeedTime,
  home3AgentName,
  home3EmptyState,
  homeFileKey,
  visibleFeedRows,
  visibleStates,
  type Home3ReadState,
  type Home3Event,
  type Home3StateFilter,
} from '../../../lib/home3-feed';
import { useTranslation } from '../../../lib/i18n';
import { ENTITY_TYPE, useGenericBi } from '../../../lib/use-generic-bi';
import { useAgentWorkspaceFiles } from '../../../lib/agent-workspace-files';
import { openFileForRow } from '../../../lib/doc-viewer-presenter';
import { useWorkspaceGoals } from '../../../lib/goals';
import { patchRoutine, useRoutines } from '../../../lib/routines';
import { useBasePath } from '../../../lib/use-base-path';
import { useAgentName } from '../../../lib/use-agent-name';
import { fileNewTabLink } from '../FileArtifactCard';
import { LiveGoalDetailsModal } from '../goals/GoalDetailsModal';
import { RoutineDetailModal } from '../routines/RoutineDetailModal';
import { Modal } from '../../Modal';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import { TaskDetailModal } from '../tasks/TaskDetailModal';
import { OutreachThreadModal } from '../tasks/OutreachThreadModal';
import { TaskSentOutreachSection } from '../tasks/TaskSentOutreachSection';
import type { WebTask } from '../../../lib/tasks';
import type { SentOutreachSummary } from '../../../lib/outreach-rows';
import { Badge, Button, Card, OverflowMenu, Typography } from '../ui';
import { AgentDot } from '../../AgentDot';
import { Home3EmptyState } from './Home3EmptyState';
import { Home3FeedRow } from './Home3FeedRow';
import styles from './Home3Feed.module.css';

export const HOME3_PAGE = 'home_overview';

/**
 * The Overview home's "All" list — every kind of item, newest first (Figma `19106:277489`). A category
 * tile opens that category's own page instead (`Home3CategoryView`), so this list is never narrowed to
 * one destination.
 *
 * The status picker beside the title narrows the rows; "Show more" at the card's foot reveals
 * `HOME3_SHOW_MORE_STEP` more at a time.
 *
 * ## A row opens the REAL drill-in when it has one
 *
 * A row backed by a task pushes the shared `TaskDetailModal` onto `TaskSidePanelContext` — the same
 * drill-in the Tasks list, the island's quick view and the goal plan open. A goal, routine or file row
 * opens that item's own drill-in. Only a row with neither (an app an agent built) falls back to this
 * screen's own small modal.
 */
export function Home3Feed({
  events,
  narrow,
  viewportNarrow,
  agentNames,
  spaceNames,
  readState,
  regionId,
}: {
  /** Every feed row — the counts need them all even when the status picker narrows the rows. */
  events: Home3Event[];
  /** Measured on the screen's own container, never the viewport. See `Home3Body`. */
  narrow: boolean;
  /** The true VIEWPORT narrow signal (`useViewportNarrow`, 720) — a desktop with the chat docked keeps
   *  its desktop treatment even when the pane is squeezed under 700. The status picker's desktop-only
   *  badge size reads this, not `narrow`, so the docked chat can't drop it to the mobile size. */
  viewportNarrow: boolean;
  agentNames: Record<string, string>;
  /** Space id → name, for the rows' second line. Resolved by the screen, which reads the workspaces. */
  spaceNames: Record<string, string>;
  /** Whether the rows can be trusted yet. `ready` is the only state that may say "no activity". */
  readState: Home3ReadState;
  /** This region's DOM id — the cards' `aria-controls` target. */
  regionId: string;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const maestro = useAgentName();
  const { openModal, closeModal } = useTaskSidePanel();
  const [state, setState] = useState<Home3StateFilter>(null);
  const [showMoreTaps, setShowMoreTaps] = useState(0);
  const [opened, setOpened] = useState<Home3Event | null>(null);
  // An ITEM row opens the item's own drill-in — the one its screen opens. These reads are the same cache
  // entries `Home3Body` built the rows from, so the lookup costs nothing.
  const basePath = useBasePath();
  const goalsState = useWorkspaceGoals();
  const { state: routinesState } = useRoutines();
  const filesState = useAgentWorkspaceFiles();
  const [openRoutineId, setOpenRoutineId] = useState<string | null>(null);
  const openRoutine =
    openRoutineId && routinesState.kind === 'ready' ? routinesState.routines.find((r) => r.id === openRoutineId) : undefined;

  /** Flips a routine row's switch — the same write, BI name and rollback the Routines screen uses. */
  const toggleRoutine = (event: Home3Event, next: boolean) => {
    const id = event.item?.kind === 'routine' ? event.item.id : undefined;
    const routine = id && routinesState.kind === 'ready' ? routinesState.routines.find((r) => r.id === id) : undefined;
    if (!routine) return;
    logPageClickBi(
      { page_name: HOME3_PAGE, button_name: 'toggle_routine', state: next ? 'on' : 'off' },
      { componentName: 'feed-row-routines', entity: { id: routine.id, type: ENTITY_TYPE.pulse } },
    );
    void patchRoutine(routine, { paused: !next });
  };

  /** Opens an item row. Returns false when the item is not loaded, so the caller falls back. */
  const openItem = (event: Home3Event): boolean => {
    const item = event.item;
    if (!item) return false;
    if (item.kind === 'goal') {
      const goal = goalsState.status === 'ready' ? goalsState.goals.find((g) => g.id === item.id) : undefined;
      if (!goal) return false;
      openModal(<LiveGoalDetailsModal workspaceId={goal.workspaceId} goalId={goal.id} initialGoal={goal} />, goal.id);
      return true;
    }
    if (item.kind === 'routine') {
      if (routinesState.kind !== 'ready' || !routinesState.routines.some((r) => r.id === item.id)) return false;
      setOpenRoutineId(item.id);
      return true;
    }
    const file = filesState.status === 'ready' ? filesState.items.find((f) => homeFileKey(f) === item.id) : undefined;
    if (!file) return false;
    const fileHref = file.workspaceSlug ? fileNewTabLink(file.workspaceSlug, file, basePath) : undefined;
    openFileForRow(file, HOME3_PAGE, fileHref, file.workspaceName);
    return true;
  };

  const states = useMemo(() => visibleStates(events), [events]);
  const rows = useMemo(() => filterFeed(events, state, null), [events, state]);
  const shownCount = visibleFeedRows(feedRowCap(narrow), showMoreTaps, rows.length);
  const agents = useMemo(() => agentCount(events), [events]);
  // The distinct acting agents, in the order they appear — the marks for the footnote. Same set
  // `agentCount` sizes, so the cluster and the number can never disagree.
  const agentIds = useMemo(() => {
    const seen: string[] = [];
    for (const event of events) {
      const id = event.subtitle.kind === 'agent' ? event.subtitle.agentId : '';
      if (id && !seen.includes(id)) seen.push(id);
    }
    return seen;
  }, [events]);
  const empty = emptyFeedMessage(state, null);
  // The designed empty state takes the whole card: the heading, picker and footnote all go, as the frame
  // draws it (Figma 20018:112067).
  const emptyState = home3EmptyState(readState, state, rows.length);

  // "47 events across 7 agents" — two counted clauses, each with its own plural form, joined by a key
  // the translator owns. An account with no agent-acted row shows the events half alone rather than
  // "across 0 agents", which is the same graceful-at-zero rule the empty line follows.
  const eventsLabel = t(events.length === 1 ? 'home.overview.feed.eventsOne' : 'home.overview.feed.eventsOther', { count: events.length });
  const agentsLabel = t(agents === 1 ? 'home.overview.feed.agentsOne' : 'home.overview.feed.agentsOther', { count: agents });
  const meta =
    readState !== 'ready' ? '' : agents > 0 ? t('home.overview.feed.meta', { events: eventsLabel, agents: agentsLabel }) : eventsLabel;

  const title = t('home.overview.recentActivity');

  const pickState = (next: Home3StateFilter) => {
    setState(next);
    setShowMoreTaps(0);
    logPageClickBi({ page_name: HOME3_PAGE, button_name: `filter_state_${next ?? 'all'}` });
  };

  return (
    <section id={regionId} className={styles.feed} aria-label={title}>
      {!emptyState && (
        <div className={styles.headRow}>
          {/* Display 05 — Archivo SemiBold 16 (Design QA, Caro). The DS role, which the legacy `.sym-*`
            scale has no alias for: its 16 is the UI face and this line is the display one. */}
          <Typography variant="display-05" component="h2">
            {title}
          </Typography>
          {/* Tier 1, as a picker rather than a chip strip: a row of chips can only state the current
            value by being scanned.
            
            The trigger reads **"Status"** while nothing is filtered — which is what the frame draws
            (Figma 19697:59395) — and the VALUE once something is, so the control still answers "what
            am I looking at" without a second glance. Naming it "Status" in both states would have
            hidden an active filter behind a static word. Outlined, because the frame gives it an
            edge; it was the ghost type. `onClear` appears only away from the unfiltered state. */}
          <OverflowMenu
            ariaLabel={t('home.overview.filterState')}
            label={state ? t(HOME3_STATE_LABEL_KEY[state]) : t('home.overview.statusLabel')}
            triggerSize="small"
            /* DESKTOP: the open menu is the Figma Card Box (20018-107017) — a sunken card at padding 20/10,
             radius 20, its rows 16 apart, each row taking a grey ghost fill on hover — not the default
             compact Action Menu. Mobile web keeps the standard popover. */
            menuClassName={viewportNarrow ? undefined : styles.statusMenu}
            /* The frame is a Symphony Button at Skin=Standard, **Type=Ghost**, 86x30 (Design QA, Caro —
             Figma 20018:106749), which is `variant="text"` on our own primitive at `size="small"`.
             It was `outlined`: a boxed control beside a plain heading read as the loudest thing on the
             row, where the design wants the filter to recede until it is carrying a value. */
            triggerVariant="text"
            /* The picker sits at the row's trailing edge, so the menu RIGHT-aligns to the trigger (its
             default for a labelled picker is `start`, which left-aligned the menu and ran it off the
             pane's right edge — the misalignment). `end` opens it leftward, flush under "Status". */
            align="end"
            selectedKey={state ?? 'all'}
            /* No ✕ clear affordance: the Figma selected trigger (20102-59105) is just the value + chevron,
             and "All" in the open menu is the way back to unfiltered. */
            actions={[
              // "All" is not a status, so it stays plain text — badging it would invent a fourth state. No
              // count (the Figma shows bare labels). Wrapped so its hover (ink + underline, Figma 20114-63662)
              // can be styled; it is the one option that reads as a text button rather than a badge.
              { key: 'all', label: <span className={styles.allOption}>{t('home.overview.filter.all')}</span>, onSelect: () => pickState(null) },
              // ...and each real state wears its OWN badge, the same one its rows wear (Figma: the open
              // picker is a column of coloured badges). `home3Badge` is what maps a state to the DS
              // variant, so the colour here cannot drift from the colour in the list below.
              //
              // The picker badges are the DS **Tiny** size (20px, 12/16 SemiBold, -2%) on both viewports —
              // Figma 20018-107017. The hover is NOT on the badge: this frame highlights the whole ROW with
              // a grey ghost fill (see `.statusMenu` row :hover), so the badge stays inert and carries no
              // `hoverable` treatment.
              ...states.map((value) => ({
                key: value,
                label: (
                  <Badge
                    variant={home3StateBadge(value).variant}
                    size="tiny"
                    label={t(HOME3_STATE_LABEL_KEY[value])}
                  />
                ),
                onSelect: () => pickState(value),
              })),
            ]}
          />
        </div>
      )}

      {/* The DS SUNKEN card — white on the stone page — at the design's L size (Figma 19106:277381:
          "Skin=Sunken, size=L", radius 20, padding 20). It was `outline`/`sm`: a hairline with no fill,
          which on the light page left the list looking like bare page rather than a surface. `sunken`
          is the DS's own answer for "white card on a stone surface", so it flips correctly in dark
          mode too instead of pinning a white. */}
      <Card size="lg">
        {emptyState ? (
          <Home3EmptyState empty={emptyState} />
        ) : readState !== 'ready' ? (
          // NOT the empty line: a read that is still running, or that failed, has not established that
          // nothing happened. Saying "no activity" here would be the screen inventing an answer.
          <Typography variant="p2" color="secondary" className={styles.empty}>
            {t(readState === 'error' ? 'home.overview.readFailed' : 'home.overview.loading')}
          </Typography>
        ) : rows.length === 0 ? (
          // Never a blank card: the line says which filter produced the emptiness, naming only the
          // halves that are actually set.
          <Typography variant="p2" color="secondary" className={styles.empty}>
            {t(empty.key, { filters: empty.filterKeys.map((key) => t(key)).join(' ') })}
          </Typography>
        ) : (
          <div className={styles.rows}>
            {rows.slice(0, shownCount).map((event) => (
              <Home3FeedRow
                key={event.id}
                event={event}
                agentName={home3AgentName(event, agentNames)}
                spaceName={(event.workspaceId && spaceNames[event.workspaceId]) || ''}
                showCategory
                maestroName={maestro.short}
                {...(event.toggle ? { onToggle: (next: boolean) => toggleRoutine(event, next) } : {})}
                onOpen={() => {
                  logPageClickBi(
                    { page_name: HOME3_PAGE, button_name: 'open_event', ...(event.taskId ? { task_id: event.taskId } : {}) },
                    {
                      componentName: `feed-row-${event.destination}`,
                      ...(event.taskId ? { entity: { id: event.taskId, type: ENTITY_TYPE.task } } : {}),
                    },
                  );
                  // A MESSAGES row is not a task, and the generic detail has nothing useful to say
                  // about one — it showed a date, a channel and a badge, with no correspondence, no
                  // reply and no way to open a shared file (review, P1). Both arms open a drill-in
                  // that already exists.
                  if (event.outreach?.kind === 'inbound') {
                    openModal(<OutreachThreadModal task={event.outreach.task as WebTask} />, event.id);
                  } else if (event.outreach?.kind === 'sent') {
                    openModal(
                      // Titled by the PERSON, with the same translated fallback the row uses. The row's
                      // own title is the last message now, so the name comes off its second line. With a
                      // raw name an unresolved recipient gave the dialog an empty header and so no
                      // accessible name at all (review).
                      <Modal
                        open
                        onClose={closeModal}
                        title={sentThreadTitle(event, t)}
                        maxHeightPx={650}
                        scrollBody
                      >
                        <TaskSentOutreachSection outreaches={[event.outreach.thread as SentOutreachSummary]} />
                      </Modal>,
                      event.id,
                    );
                  } else if (event.taskId) {
                    openModal(<TaskDetailModal taskId={event.taskId} />, event.taskId);
                  } else if (!openItem(event)) {
                    setOpened(event);
                  }
                }}
              />
            ))}
          </div>
        )}

        {readState === 'ready' && shownCount < rows.length && (
          <div className={styles.seeAllRow}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setShowMoreTaps((n) => n + 1);
                logPageClickBi({ page_name: HOME3_PAGE, button_name: 'show_more' });
              }}
            >
              {t('home.overview.showMore')}
            </Button>
          </div>
        )}
      </Card>

      {/* "47 events across 7 agents" — the whole list's footnote, under the card. */}
      {meta && !emptyState && (
        <p className={styles.meta}>
          {/* The agents behind the window, as their real marks — the design leads this line with the
              overlapping dot cluster (Figma 19106:277489's foot). Their own no-gap box, because the
              line's `gap` would cancel the negative margin and leave them flush; same construction,
              and the same -6 on a 20px dot, as the brief card's cluster and the island's. */}
          {agentIds.length > 0 && (
            <span className={styles.metaAvatars}>
              {agentIds.map((id) => (
                <span key={id} className={styles.metaAvatar}>
                  <AgentDot id={id} size={20} ringColor="var(--ds-surface-standard-bg)" />
                </span>
              ))}
            </span>
          )}
          <Typography variant="p3" color="secondary" component="span">
            {meta}
          </Typography>
        </p>
      )}

      {openRoutine && (
        <RoutineDetailModal
          key={openRoutine.id}
          routine={openRoutine}
          spaceName={openRoutine.workspaceId ? spaceNames[openRoutine.workspaceId] : undefined}
          onClose={() => setOpenRoutineId(null)}
        />
      )}

      {/* Only for a row with no drill-in of its own. */}
      <Modal open={!!opened} onClose={() => setOpened(null)} title={opened?.title} maxHeightPx={650} scrollBody>
        {opened && (
          <div className={styles.body}>
            <Typography variant="p3" color="secondary">
              {formatFeedTime(opened.at)}
            </Typography>
            <Typography variant="p2" color="secondary" className={styles.subRow}>
              {opened.subtitle.kind === 'agent'
                ? home3AgentName(opened, agentNames)
                : opened.subtitle.kind === 'contact'
                  ? [opened.subtitle.name, opened.subtitle.channel].filter(Boolean).join(' | ')
                  : opened.subtitle.kind === 'maestro'
                    ? maestro.short
                    : ''}
              <Badge variant={opened.badge.variant} size="tiny" label={t(opened.badge.labelKey)} />
            </Typography>
          </div>
        )}
      </Modal>
    </section>
  );
}

/** The sent-thread dialog's title: the person the thread is with, or the unresolved-recipient fallback. */
function sentThreadTitle(event: Home3Event, t: (key: string) => string): string {
  const sub = event.subtitle;
  if (sub.kind === 'contact') {
    if (sub.name) return sub.name;
    if (sub.nameKey) return t(sub.nameKey);
  }
  return event.titleKey ? t(event.titleKey) : event.title;
}
